import { DragEvent, useEffect, useRef, useState } from "react";
import styles from "./UtilityLogoUpload.module.scss";
import { UploadUnfiledIcon } from "../../../../../assets/icons";

interface UtilityLogoUploadProps {
	/** A picked File, or the URL of the logo already stored on the utility. */
	value: File | string | null;
	onChange: (file: File | null) => void;
	error?: string;
}

export function UtilityLogoUpload({ value, onChange, error }: Readonly<UtilityLogoUploadProps>) {
	const [isDragging, setIsDragging] = useState(false);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (!value) {
			setPreviewUrl(null);
			return;
		}

		if (typeof value === "string") {
			setPreviewUrl(value);
			return;
		}

		const url = URL.createObjectURL(value);
		setPreviewUrl(url);
		return () => URL.revokeObjectURL(url);
	}, [value]);

	const handleDrop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragging(false);

		const file = event.dataTransfer.files?.[0];
		if (file) onChange(file);
	};

	return (
		<div className={styles.container}>
			<div
				className={`${styles.dropzone} ${isDragging ? styles.dragging : ""} ${error ? styles.invalid : ""}`}
				onClick={() => inputRef.current?.click()}
				onDragOver={event => {
					event.preventDefault();
					setIsDragging(true);
				}}
				onDragLeave={() => setIsDragging(false)}
				onDrop={handleDrop}
			>
				{value && previewUrl ? (
					<div className={styles.preview}>
						{/* A stored logo has no file name to show — only a picked File does. */}
						<img className={styles.previewImage} src={previewUrl} alt="Utility logo" />
						{value instanceof File && <span className={styles.fileName}>{value.name}</span>}
						<button
							type="button"
							className={styles.removeButton}
							onClick={event => {
								event.stopPropagation();
								onChange(null);
							}}
						>
							Remove
						</button>
					</div>
				) : (
					<>
						<span className={styles.uploadIcon}>
							<UploadUnfiledIcon />
						</span>
						<span className={styles.hint}>
							Drag and drop file here or <span className={styles.browse}>Browse</span> to upload
						</span>
						{/* Mirrors the backend's ImageFileValidator and its 1 MB cap. */}
						<span className={styles.limits}>
							Supported formats are PNG, JPG and SVG. Maximum file size is 1 MB.
						</span>
					</>
				)}

				<input
					ref={inputRef}
					type="file"
					accept=".png,.jpg,.jpeg,.svg"
					hidden
					onChange={event => onChange(event.target.files?.[0] ?? null)}
				/>
			</div>
			{error && <span className={styles.error}>{error}</span>}
		</div>
	);
}
